import type { Components } from "react-markdown";
import { Link } from "react-router-dom";
import { resolveDocHref } from "./docPathUtils";

const linkClassName = "text-primary font-medium underline underline-offset-2 hover:opacity-90";

/** Tailwind-only styling for rendered repo docs (no @tailwindcss/typography). */
export function createDocMarkdownComponents(currentDocKey: string): Components {
  return {
    h1: ({ children }) => (
      <h1 className="text-2xl font-bold text-slate-900 mt-8 mb-3 first:mt-0 border-b border-slate-200 pb-2">
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-xl font-semibold text-slate-900 mt-6 mb-2">{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-lg font-semibold text-slate-800 mt-4 mb-2">{children}</h3>
    ),
    p: ({ children }) => <p className="text-slate-700 leading-relaxed mb-3">{children}</p>,
    ul: ({ children }) => (
      <ul className="list-disc pl-6 mb-3 space-y-1 text-slate-700">{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className="list-decimal pl-6 mb-3 space-y-1 text-slate-700">{children}</ol>
    ),
    li: ({ children }) => <li className="leading-relaxed">{children}</li>,
    a: ({ href, children }) => {
      const resolved = resolveDocHref(href, currentDocKey);
      if (resolved?.startsWith("/documentation")) {
        return (
          <Link to={resolved} className={linkClassName}>
            {children}
          </Link>
        );
      }
      if (href?.startsWith("http")) {
        return (
          <a href={href} className={linkClassName} target="_blank" rel="noreferrer">
            {children}
          </a>
        );
      }
      if (href?.endsWith(".md")) {
        return (
          <span className="text-slate-600" title="Developer reference (repo docs only)">
            {children}
          </span>
        );
      }
      return (
        <a href={href} className={linkClassName}>
          {children}
        </a>
      );
    },
    code: ({ className, children, ...props }) => {
      const inline = !className;
      if (inline) {
        return (
          <code
            className="rounded bg-slate-100 px-1.5 py-0.5 text-sm font-mono text-slate-800"
            {...props}
          >
            {children}
          </code>
        );
      }
      return (
        <code className={`block text-sm font-mono text-slate-800 ${className ?? ""}`} {...props}>
          {children}
        </code>
      );
    },
    pre: ({ children }) => (
      <pre className="overflow-x-auto rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm mb-4">
        {children}
      </pre>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-primary/40 pl-4 italic text-slate-600 my-4">
        {children}
      </blockquote>
    ),
    table: ({ children }) => (
      <div className="overflow-x-auto mb-4">
        <table className="w-full text-sm border-collapse border border-slate-200">{children}</table>
      </div>
    ),
    thead: ({ children }) => <thead className="bg-slate-100">{children}</thead>,
    th: ({ children }) => (
      <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-800">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="border border-slate-200 px-3 py-2 text-slate-700">{children}</td>
    ),
    hr: () => <hr className="my-8 border-slate-200" />,
    strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
  };
}
